/**
 * "Tap to enable alert sound" (Decision 18; built 2026-10-31).
 *
 * A browser keeps web audio silent until the person has tapped the page:
 * iOS always, Chrome on a laptop until the first click. Until then the
 * chime for a new alert, and the fire siren with it, play nothing, and
 * nothing on screen said so. This chip says so, once, while the
 * console's shared AudioContext (`state/audio-context.ts`) is held
 * suspended. A tap resumes it and plays a short test chime, so the
 * person hears that it worked; a tap anywhere else on the page unlocks
 * it too, and the chip goes.
 *
 * NEVER WHEN IT WOULD BE WRONG: not while sound already works, and not
 * when the person has turned it off (alerts muted in the bar, with the
 * siren set to follow the mute or never to sound). A muted chime with the
 * siren set to "always" still shows it: the siren is the sound that
 * matters.
 *
 * In the flow of the shell under the banners, like the new-build banner,
 * never over anything: the toasts and the fire-hold notice own the
 * bottom of the screen.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  audioUnlockState,
  sharedAudioContext,
  subscribeAudioUnlock,
  unlockAudio,
} from '../state/audio-context';
import { playTestChime } from '../state/alert-audio';
import { useAlertNotificationsStable } from '../state/useAlertNotifications';
import { useUserPreferences } from '../state/useUserPreferences';
import { IconVolume2 } from '../design-system/icons';
import './SoundUnlockChip.css';

/** How long a new context may take to start by itself before the chip
 *  calls it held. Every browser reports a new context `suspended` for a
 *  moment; one that is allowed to play is running well inside this, so
 *  the chip does not flash up on a page where sound already works. */
const START_GRACE_MS = 1500;

export function SoundUnlockChip({ graceMs = START_GRACE_MS }: { graceMs?: number } = {}) {
  const { muted } = useAlertNotificationsStable();
  const { prefs } = useUserPreferences();
  const wanted = !muted || prefs.fireAlarmAudio === 'always';
  const [graceOver, setGraceOver] = useState(graceMs <= 0);

  // Made here, at once, rather than on the first chime: a context made
  // before any tap starts suspended, which is the one way to know ahead
  // of an alert that it would be silent.
  useEffect(() => {
    if (!wanted) return undefined;
    sharedAudioContext();
    if (graceMs <= 0) return undefined;
    const t = window.setTimeout(() => setGraceOver(true), graceMs);
    return () => window.clearTimeout(t);
  }, [wanted, graceMs]);

  const state = useSyncExternalStore(subscribeAudioUnlock, audioUnlockState, () => 'unavailable');
  if (!wanted || !graceOver || state !== 'locked') return null;

  return (
    <div className="nx-sound-chip-row">
      <button
        type="button"
        className="nx-sound-chip"
        data-testid="sound-unlock-chip"
        onClick={() => {
          void unlockAudio().then((running) => {
            if (running) playTestChime();
          });
        }}
      >
        <IconVolume2 size={15} />
        <span>Tap to enable alert sound</span>
      </button>
    </div>
  );
}
